import React, { useEffect, useState, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";
import { GET } from "../service/CRUDService";
import EntityConstants from "../configs/EntityConstants";
import { isPublicVerificationPortalRoute } from "../configs/featureFlags";
import { clearAuthLocalStorage } from "../utils/clearAuthStorage";

const AuthContext = React.createContext({
  isLoggedIn: false,

  isLoading: true,

  login: () => {},

  logout: () => {},

  createUserProfile: (id) => {},
  user_profile_id: null,

  createUserProfileDetails: (userDetails) => {},
  userDetails: null,

  createTenantId: (tenant_id) => {},
  tenant_id: null,

  createTenantName: (tenant_name) => {},
  tenant_name: null,
  isSignUpHandler: (isSignUp) => {},
  isSignUp: null,

  isGoogleLogin: false,
  googlelogin: () => {},

  googlelogout: () => {},

  isMicrosoftLogin: false,
  microsoftlogin: () => {},
  microsoftlogout: () => {},

  hasDiscoveryReachedBottomHandler: (bool) => {},
  hasDiscoveryReachedBottom: false,

  lastVisitedPathHandler: () => {},
  lastVisitedPath: null,

  IssuedOffsetHandler: (offset) => {},
  issuedOffset: null,

  isFormDataChangedHandler: (bool) => {},
  isFormDataChanged: null,

  isConfirmationModalOpenHandler: (bool) => {},
  isConfirmationModalOpen: null,

  pendingNavigationHandler: (to) => {},
  pendingNavigation: null,

  sidebarComponents: [],
  setSidebarComponents: () => {},

  userType: null,
  setUserType: () => {},

  selectedLanguage: "en",
  setSelectedLanguage: () => {},
});

export const AuthContextProvider = (props) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user_profile_id, setUser_profile_id] = useState(null);
  const [tenant_id, setTenant_id] = useState(null);
  const [tenant_name, setTenant_Name] = useState(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isGoogleLogin, setIsGoogleLogin] = useState(false);
  const [isMicrosoftLogin, setIsMicrosoftLogin] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [hasDiscoveryReachedBottom, setHasDiscoveryReachedBottom] =
    useState(false);
  const [lastVisitedPath, setLastVisitedPath] = useState(null);
  const [issuedOffset, setIssuedOffset] = useState(
    Number(localStorage.getItem("transactionOffset")) || null,
  );
  const [isFormDataChanged, setIsFormDataChanged] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [sidebarComponents, setSidebarComponents] = useState([]);
  const [userType, setUserType] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const saved = localStorage.getItem("selectedLanguage");
    if (saved) return saved;
    try {
      const stored = JSON.parse(
        localStorage.getItem("availableLanguages") || "null",
      );
      if (stored && stored.length > 0) return stored[0].value;
    } catch (e) {
      // ignore parse errors
    }
    return "en";
  });

  useEffect(() => {
    const logoutReason = localStorage.getItem("logout_reason");
    if (logoutReason === "session_timeout") {
      localStorage.removeItem("logout_reason");
      if (!isPublicVerificationPortalRoute()) {
        toast.error(
          "You have been logged out due to session timeout. Please sign in again.",
          {
            duration: 6000,
          },
        );
      }
    }
  }, []);

  useEffect(() => {
    const checkAuthOnMount = () => {
      try {
        const token = localStorage.getItem("auth_token");
        const storedUserType = localStorage.getItem("userType");

        if (token) {
          setIsLoggedIn(true);

          if (storedUserType) {
            setUserType(storedUserType.toLowerCase());
          }

          // You can also restore other user data if stored in localStorage
          const storedUserDetails = localStorage.getItem("userDetails");
          let parsedDetails = null;
          if (storedUserDetails) {
            try {
              parsedDetails = JSON.parse(storedUserDetails);
              setUserDetails(parsedDetails);
            } catch (e) {
              console.error("Error parsing stored user details:", e);
            }
          }

          const storedSidebarComponents =
            localStorage.getItem("sidebarComponents");
          if (storedSidebarComponents) {
            try {
              setSidebarComponents(JSON.parse(storedSidebarComponents));
            } catch (e) {
              console.error("Error parsing stored sidebar components:", e);
            }
          } else if (Array.isArray(parsedDetails?.sidebar_components)) {
            setSidebarComponents(parsedDetails.sidebar_components);
            localStorage.setItem(
              "sidebarComponents",
              JSON.stringify(parsedDetails.sidebar_components),
            );
          }

          const storedUserId = localStorage.getItem("user_profile_id");
          if (storedUserId) {
            setUser_profile_id(storedUserId);
          }

          const storedTenantId = localStorage.getItem("tenant_id");
          if (storedTenantId) {
            setTenant_id(storedTenantId);
          }

          const storedTenantName = localStorage.getItem("tenant_name");
          if (storedTenantName) {
            setTenant_Name(storedTenantName);
          }
        } else {
          resetUserDetails();
        }
      } catch (error) {
        console.error("Error during authentication check:", error);
        resetUserDetails();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthOnMount();
  }, []);

  useEffect(() => {
    lastVisitedPath && sessionStorage.setItem("previousPath", lastVisitedPath);
  }, [lastVisitedPath]);

  useEffect(() => {
    !localStorage.getItem("auth_token") && resetUserDetails();
  }, [localStorage.getItem("auth_token")]);

  const persistSidebarComponents = useCallback((components) => {
    const next = Array.isArray(components) ? components : [];
    setSidebarComponents(next);
    localStorage.setItem("sidebarComponents", JSON.stringify(next));
  }, []);

  const resetUserDetails = useCallback(() => {
    setIsLoggedIn(false);
    setUser_profile_id(null);
    setTenant_id(null);
    setTenant_Name(null);
    setIsGoogleLogin(false);
    setIsMicrosoftLogin(false);
    setUserDetails({});
    setSidebarComponents([]);
    setUserType(null);
    localStorage.removeItem("sidebarComponents");
  }, []);

  const clearAuthQuietly = () => {
    // Preserve portal branding / language caches on the public portal.
    clearAuthLocalStorage();
    resetUserDetails();
    setIsLoading(false);
  };

  // Force logout helper
  const forceLogout = (reason = "manual") => {
    if (reason === "session_expired" && isPublicVerificationPortalRoute()) {
      clearAuthQuietly();
      return;
    }

    if (reason === "session_expired") {
      localStorage.setItem("logout_reason", "session_timeout");
    }
    localStorage.clear();
    sessionStorage.clear();
    if (reason === "session_expired") {
      localStorage.setItem("logout_reason", "session_timeout");
    }
    localStorage.setItem(
      "auth_event",
      JSON.stringify({ type: "LOGOUT", reason: reason, t: Date.now() }),
    );
    window.location.replace("/");
  };

  const isRefreshTokenExpired = () => {
    try {
      const token = localStorage.getItem("refresh_token");
      if (!token) return true;
      const { exp } = jwtDecode(token);
      return Date.now() >= exp * 1000;
    } catch {
      return true;
    }
  };

  // Initial session check only
  const validateSession = useCallback(async () => {
    const access = localStorage.getItem("auth_token");
    const refresh = localStorage.getItem("refresh_token");

    // No tokens → stop without redirect
    if (!access && !refresh) {
      setIsLoading(false);
      return false;
    }

    if (isRefreshTokenExpired()) {
      forceLogout("session_expired");
      return false;
    }

    try {
      const response = await GET(EntityConstants.CURRENT_USER);
      if (response.ok) {
        setIsLoggedIn(true);
        return true;
      }
      throw new Error("Access token invalid");
    } catch {
      forceLogout("session_expired");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [resetUserDetails]);

  useEffect(() => {
    validateSession();
  }, [validateSession]);

  // Cross-tab logout sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "auth_event") {
        try {
          const evt = JSON.parse(e.newValue || "{}");
          if (evt.type === "LOGOUT") {
            const logoutReason = evt.reason || "manual";
            forceLogout(logoutReason);
          }
        } catch {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [forceLogout]);

  const createUserProfile = (id) => {
    setUser_profile_id(id);
    localStorage.setItem("user_profile_id", id);
  };

  const createUserProfileDetails = (userDetails) => {
    if (typeof userDetails === "object") {
      setUserDetails(userDetails);
      setUserType(userDetails.type?.toLowerCase() || null);
      if (Array.isArray(userDetails.sidebar_components)) {
        persistSidebarComponents(userDetails.sidebar_components);
      }

      localStorage.setItem("userDetails", JSON.stringify(userDetails));
      if (userDetails.type) {
        localStorage.setItem("userType", userDetails.type.toLowerCase());
      }
    } else {
      setUserDetails({});
      setUserType(null);
      throw new Error(
        '"object" / "{}" expected to create user profile details',
      );
    }
  };

  const loginHandler = (userData) => {
    setIsLoggedIn(true);
    setIsLoading(false);

    if (userData) {
      setUserType(userData.type?.toLowerCase());
      if (Array.isArray(userData.sidebar_components)) {
        persistSidebarComponents(userData.sidebar_components);
      }
      localStorage.setItem("userType", userData.type?.toLowerCase());
      localStorage.setItem("userDetails", JSON.stringify(userData));
    }
  };

  const createTenantId = (tenant_id) => {
    setTenant_id(tenant_id);
    localStorage.setItem("tenant_id", tenant_id);
  };

  const createTenantName = (tenant_name) => {
    setTenant_Name(tenant_name);
    localStorage.setItem("tenant_name", tenant_name);
  };

  const isSignUpHandler = (bool) => {
    setIsSignUp(bool);
  };

  const logoutHandler = () => forceLogout("manual");

  const googleloginHandler = () => {
    setIsGoogleLogin(true);
  };

  const googlelogoutHandler = () => {
    setIsGoogleLogin(false);
  };

  const microsoftLoginHandler = () => {
    setIsMicrosoftLogin(true);
  };

  const microsoftLogoutHandler = () => {
    setIsMicrosoftLogin(false);
  };

  const hasDiscoveryReachedBottomHandler = (bool) => {
    setHasDiscoveryReachedBottom(bool);
  };

  const lastVisitedPathHandler = (lastVisitedPath_) => {
    setLastVisitedPath(lastVisitedPath_);
  };

  const IssuedOffsetHandler = (offset) => {
    setIssuedOffset(offset);
    localStorage.setItem("transactionOffset", offset);
  };

  const isFormDataChangedHandler = (bool) => {
    setIsFormDataChanged(bool);
  };

  const isConfirmationModalOpenHandler = (bool) => {
    setIsConfirmationModalOpen(bool);
  };

  const pendingNavigationHandler = (to) => {
    setPendingNavigation(to);
  };

  const contextValue = {
    isLoggedIn: isLoggedIn,
    isLoading: isLoading,
    login: loginHandler,

    logout: logoutHandler,

    createUserProfile: createUserProfile,
    user_profile_id: user_profile_id,

    createUserProfileDetails: createUserProfileDetails,
    userDetails: userDetails,

    createTenantId: createTenantId,
    tenant_id: tenant_id,

    createTenantName: createTenantName,
    tenant_name: tenant_name,

    isSignUpHandler: isSignUpHandler,
    isSignUp: isSignUp,

    googlelogin: googleloginHandler,

    googlelogout: googlelogoutHandler,

    hasDiscoveryReachedBottomHandler: hasDiscoveryReachedBottomHandler,
    hasDiscoveryReachedBottom: hasDiscoveryReachedBottom,

    lastVisitedPathHandler: lastVisitedPathHandler,
    lastVisitedPath: lastVisitedPath,

    IssuedOffsetHandler: IssuedOffsetHandler,
    issuedOffset: issuedOffset,

    microsoftLogin: microsoftLoginHandler,
    microsoftLogout: microsoftLogoutHandler,

    isFormDataChangedHandler,
    isFormDataChanged,

    isConfirmationModalOpenHandler,
    isConfirmationModalOpen,

    pendingNavigationHandler,
    pendingNavigation,

    sidebarComponents,
    setSidebarComponents: persistSidebarComponents,

    userType,
    setUserType,

    selectedLanguage,
    setSelectedLanguage: (lang) => {
      setSelectedLanguage(lang);
      localStorage.setItem("selectedLanguage", lang);
    },
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
